import React, { useState } from 'react';
import { useLanguage } from '../contexts/LanguageContext';
import { useNotification } from '../contexts/NotificationContext';

type CellStatus = 'empty' | 'ship' | 'hit' | 'miss';

interface Cell {
  status: CellStatus;
  row: number;
  col: number;
}

interface ShipConfig {
  size: number;
  count: number;
}

const DEFAULT_GRID_SIZE = 10;
const DEFAULT_SHIPS: ShipConfig[] = [
  { size: 4, count: 1 },
  { size: 3, count: 2 },
  { size: 2, count: 3 },
  { size: 1, count: 4 },
];

export const BattleshipPage: React.FC = () => {
  const { t } = useLanguage();
  const { error, success } = useNotification();

  const [gridSize, setGridSize] = useState<number>(DEFAULT_GRID_SIZE);
  const [ships, setShips] = useState<ShipConfig[]>(DEFAULT_SHIPS);
  const [grid, setGrid] = useState<Cell[][]>(createEmptyGrid(DEFAULT_GRID_SIZE));
  const [showConfig, setShowConfig] = useState(false);

  function createEmptyGrid(size: number): Cell[][] {
    const newGrid: Cell[][] = [];
    for (let row = 0; row < size; row++) {
      newGrid[row] = [];
      for (let col = 0; col < size; col++) {
        newGrid[row][col] = { status: 'empty', row, col };
      }
    }
    return newGrid;
  }

  const generateField = () => {
    // Проверка на корректность конфигурации
    const totalCells = ships.reduce((sum, ship) => sum + ship.size * ship.count, 0);
    const maxCells = gridSize * gridSize;

    if (totalCells > maxCells * 0.5) {
      error(t('battleship.error'), t('battleship.errorTooManyShips'));
      return;
    }

    const maxShipSize = Math.max(...ships.map(s => s.size));
    if (maxShipSize > gridSize) {
      error(t('battleship.error'), t('battleship.errorShipTooLarge'));
      return;
    }

    const newGrid = createEmptyGrid(gridSize);
    let allPlaced = true;

    ships.forEach(({ size, count }) => {
      for (let i = 0; i < count; i++) {
        const placed = placeShip(newGrid, size, gridSize);
        if (!placed) {
          allPlaced = false;
        }
      }
    });

    setGrid(newGrid);

    if (!allPlaced) {
      error(t('battleship.warning'), t('battleship.warningNotAllShipsPlaced'));
    } else {
      success(t('battleship.success'), t('battleship.successFieldGenerated'));
    }
  };

  const placeShip = (grid: Cell[][], size: number, gridSizeParam: number): boolean => {
    let placed = false;
    let attempts = 0;
    const maxAttempts = 2000;

    while (!placed && attempts < maxAttempts) {
      attempts++;
      const horizontal = Math.random() > 0.5;
      const row = Math.floor(Math.random() * gridSizeParam);
      const col = Math.floor(Math.random() * gridSizeParam);

      if (canPlaceShip(grid, row, col, size, horizontal, gridSizeParam)) {
        for (let i = 0; i < size; i++) {
          if (horizontal) {
            grid[row][col + i].status = 'ship';
          } else {
            grid[row + i][col].status = 'ship';
          }
        }
        placed = true;
      }
    }

    return placed;
  };

  const canPlaceShip = (
    grid: Cell[][],
    row: number,
    col: number,
    size: number,
    horizontal: boolean,
    gridSizeParam: number
  ): boolean => {
    // Проверка выхода за границы
    if (horizontal) {
      if (col + size > gridSizeParam) return false;
    } else {
      if (row + size > gridSizeParam) return false;
    }

    // Проверка на занятость клеток и соседей
    for (let i = 0; i < size; i++) {
      const currentRow = horizontal ? row : row + i;
      const currentCol = horizontal ? col + i : col;

      // Проверка самой клетки и соседей (включая диагонали)
      for (let dr = -1; dr <= 1; dr++) {
        for (let dc = -1; dc <= 1; dc++) {
          const checkRow = currentRow + dr;
          const checkCol = currentCol + dc;

          if (
            checkRow >= 0 &&
            checkRow < gridSizeParam &&
            checkCol >= 0 &&
            checkCol < gridSizeParam
          ) {
            if (grid[checkRow][checkCol].status !== 'empty') {
              return false;
            }
          }
        }
      }
    }

    return true;
  };

  const getCellClassName = (cell: Cell) => {
    const baseClasses = 'w-6 h-6 sm:w-8 sm:h-8 md:w-10 md:h-10 border border-background-300 transition-colors flex items-center justify-center text-xs';

    switch (cell.status) {
      case 'ship':
        return `${baseClasses} bg-primary-500 hover:bg-primary-600`;
      case 'hit':
        return `${baseClasses} bg-red-500 text-white font-bold`;
      case 'miss':
        return `${baseClasses} bg-blue-200`;
      case 'empty':
      default:
        return `${baseClasses} bg-white hover:bg-background-100`;
    }
  };

  const addShipConfig = () => {
    setShips([...ships, { size: 1, count: 1 }]);
  };

  const updateShipConfig = (index: number, field: 'size' | 'count', value: number) => {
    const newShips = [...ships];
    newShips[index][field] = Math.max(1, value);
    setShips(newShips);
  };

  const removeShipConfig = (index: number) => {
    setShips(ships.filter((_, i) => i !== index));
  };

  const resetToDefault = () => {
    setGridSize(DEFAULT_GRID_SIZE);
    setShips(DEFAULT_SHIPS);
    setGrid(createEmptyGrid(DEFAULT_GRID_SIZE));
    success(t('battleship.success'), t('battleship.successConfigReset'));
  };

  return (
    <div className="container-app py-8">
      {/* Заголовок */}
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-text-primary mb-2">
          {t('battleship.title')}
        </h1>
        <p className="text-text-secondary">{t('battleship.subtitle')}</p>
      </div>

      {/* Кнопки управления */}
      <div className="mb-6 flex flex-wrap justify-center gap-3">
        <button onClick={generateField} className="btn btn-primary">
          {t('battleship.generateField')}
        </button>
        <button
          onClick={() => setShowConfig(!showConfig)}
          className="btn btn-secondary"
        >
          {showConfig ? t('battleship.hideConfig') : t('battleship.showConfig')}
        </button>
        <button onClick={resetToDefault} className="btn btn-secondary">
          {t('battleship.resetConfig')}
        </button>
      </div>

      {/* Панель настроек */}
      {showConfig && (
        <div className="mb-6 max-w-3xl mx-auto">
          <div className="card p-4 sm:p-6">
            <h3 className="text-lg font-semibold text-text-primary mb-4">
              {t('battleship.configuration')}
            </h3>

            {/* Размер поля */}
            <div className="mb-6">
              <label className="block text-sm font-medium text-text-secondary mb-2">
                {t('battleship.gridSize')}
              </label>
              <input
                type="number"
                min="5"
                max="20"
                value={gridSize}
                onChange={(e) => setGridSize(Math.max(5, Math.min(20, parseInt(e.target.value) || 5)))}
                className="input w-full sm:w-32"
              />
              <p className="text-xs text-text-tertiary mt-1">
                {t('battleship.gridSizeHint')}
              </p>
            </div>

            {/* Конфигурация кораблей */}
            <div>
              <div className="flex items-center justify-between mb-3">
                <label className="block text-sm font-medium text-text-secondary">
                  {t('battleship.shipsConfiguration')}
                </label>
                <button onClick={addShipConfig} className="btn btn-sm btn-primary">
                  + {t('battleship.addShip')}
                </button>
              </div>

              <div className="space-y-3">
                {ships.map((ship, index) => (
                  <div key={index} className="flex flex-wrap items-center gap-3 p-3 bg-background-100 rounded-lg">
                    <div className="flex-1 min-w-[120px]">
                      <label className="block text-xs text-text-tertiary mb-1">
                        {t('battleship.shipSize')}
                      </label>
                      <input
                        type="number"
                        min="1"
                        max={gridSize}
                        value={ship.size}
                        onChange={(e) => updateShipConfig(index, 'size', parseInt(e.target.value) || 1)}
                        className="input w-full"
                      />
                    </div>
                    <div className="flex-1 min-w-[120px]">
                      <label className="block text-xs text-text-tertiary mb-1">
                        {t('battleship.shipCount')}
                      </label>
                      <input
                        type="number"
                        min="1"
                        max="10"
                        value={ship.count}
                        onChange={(e) => updateShipConfig(index, 'count', parseInt(e.target.value) || 1)}
                        className="input w-full"
                      />
                    </div>
                    <button
                      onClick={() => removeShipConfig(index)}
                      className="btn btn-sm bg-red-50 text-red-600 hover:bg-red-100 mt-auto"
                      disabled={ships.length === 1}
                    >
                      {t('battleship.remove')}
                    </button>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Игровое поле */}
      <div className="flex justify-center overflow-x-auto">
        <div className="card inline-block p-4 sm:p-6">
          <div
            className="grid gap-0.5 sm:gap-1"
            style={{
              gridTemplateColumns: `repeat(${gridSize}, minmax(0, 1fr))`,
              maxWidth: '90vw'
            }}
          >
            {grid.map((row, rowIndex) =>
              row.map((cell, colIndex) => (
                <div
                  key={`${rowIndex}-${colIndex}`}
                  className={getCellClassName(cell)}
                >
                  {cell.status === 'ship' && '🚢'}
                </div>
              ))
            )}
          </div>
        </div>
      </div>

      {/* Легенда кораблей */}
      <div className="mt-8 max-w-md mx-auto">
        <div className="card p-4">
          <h3 className="text-lg font-semibold text-text-primary mb-3">
            {t('battleship.shipsLegend')}
          </h3>
          <div className="space-y-2 text-sm text-text-secondary">
            {ships.map((ship, index) => (
              <div key={index} className="flex justify-between">
                <span>
                  {t('battleship.shipOfSize')} {ship.size} {ship.size === 1 ? t('battleship.cell') : t('battleship.cells')}
                </span>
                <span className="font-semibold">{ship.count} {t('battleship.pcs')}</span>
              </div>
            ))}
          </div>
          <div className="mt-3 pt-3 border-t border-background-300 text-xs text-text-tertiary">
            {t('battleship.totalShips')}: {ships.reduce((sum, ship) => sum + ship.count, 0)}
          </div>
        </div>
      </div>
    </div>
  );
};
